<template>
  <div class="loading_spinner">
    <ProgressSpinner style="width: 50px; height: 50px" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { socket } from "../../../util/socketioService.js";

export default {
  data() {
    return {
      timestamp: null,
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    ...mapGetters("userData", [
      "userLoaded",
      "companyLoaded",
      "registrationComplete",
      "selectedCulture",
      "companyUsers",
      "companyType",
    ]),
    ...mapGetters("trainingManagement", ["trainingLoaded"]),
  },
  methods: {
    waitForAuth0() {
      return new Promise((resolve) => {
        if (!this.$auth0.isLoading.value) {
          resolve();
          return;
        }
        const unwatch = this.$watch(
          () => this.$auth0.isLoading.value,
          (loading) => {
            if (!loading) {
              unwatch();
              resolve();
            }
          }
        );
      });
    },
    async loadUser() {
      // Held now: vue-i18n removes `this.$i18n` from a component once it
      // unmounts, and this login screen can unmount during the awaits below.
      // The instance itself is the app-wide one, so setting it later still
      // changes the language everywhere.
      const i18n = this.$i18n;

      await this.waitForAuth0();
      const token = await this.$auth0.getAccessTokenSilently();
      this.setAccessToken(token);

      // gets user information from auth0 user
      const userMetadata = await this.$auth0.user.value.user_metadata;
      const userRoles = this.$auth0.user.value.user_roles;

      // Load manager console
      if (
        userRoles == "Customer - Admin" ||
        userRoles == "Customer - Contributor" ||
        userRoles == "User - General"
      ) {
        const user = await this.getUserData(userMetadata.user_id);
        if (!user?.companyIds?.length) {
          console.error("User not found or has no companies. user_id:", userMetadata?.user_id);
          return;
        }
        await this.getCompanyData(user.companyIds[0]);
        await this.getCustomersByCompany();
        this.loadInitData();

        // Load manager data
        if (userRoles != "User - General") {
          await this.getBillingData(user.companyIds[0]);
        }

        // Connect to sockets
        socket.connect();
        socket.emit("join", [user.userId, ...user.companyIds]);

        // Set user language
        if (i18n && this.selectedCulture) {
          i18n.locale = this.selectedCulture;
        }

        if (this.registrationComplete || userRoles == "User - General") {
          this.$router.push({ name: "newsfeed" });
        } else {
          this.$router.push({ name: "registration" });
        }
      }
      // Load all admin data
      else if (userRoles == "Admin - Global") {
        await this.getAdminData(userMetadata.user_id);
        await this.getCompanyData(userMetadata.companies[0]);

        socket.connect();
        socket.emit("join", [userMetadata.user_id, userMetadata.companies[0]]);

        this.$router.push({ name: "admin-accounts" });
      }
    },
    async loadInitData() {
      if (
        !this.trainingLoaded ||
        this.timestamp - Number(this.trainingLoaded) > 900
      ) {
        await this.getTrainingData();
        // A general user only ever needs their own enrollments; the
        // company-wide list is for managers.
        if (this.userType == "general") {
          this.getEnrollmentsByUser();
        } else {
          this.getEnrollments();
        }
      }
      this.getMeetings();
    },
    ...mapActions("userData", [
      "getUserData",
      "getCompanyData",
      "getCustomersByCompany",
      "userType",
    ]),
    ...mapActions("adminData", ["getAdminData"]),
    ...mapActions("billingData", ["getBillingData"]),
    ...mapActions("authData", ["setAccessToken"]),
    ...mapActions("trainingManagement", [
      "getTrainingData",
      "getEnrollments",
      "getEnrollmentsByUser",
    ]),
    ...mapActions("meetingsManagement", ["getMeetings"]),
  },
  created() {
    this.timestamp = new Date().getTime() / 1000;

    // Loads initial application data
    if (
      !this.userLoaded ||
      !this.companyLoaded ||
      this.timestamp - Number(this.userLoaded) > 1800 ||
      this.timestamp - Number(this.companyLoaded) > 1800
    ) {
      this.loadUser();
    } else {
      if (this.companyType == "business") {
        this.loadInitData();
        if (this.registrationComplete || this.userType == "general") {
          this.$router.push({ name: "newsfeed" });
        } else {
          this.$router.push({ name: "registration" });
        }
      } else if (this.companyType == "admin") {
        this.$router.push({ name: "admin-accounts" });
      }
    }
    // Change body background
    document.body.style.backgroundColor = "#F5F5F5";
  },
  beforeUnmount() {
    document.body.style.backgroundColor = "white";
  },
};
</script>

<style scoped>
.loading_spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}
</style>
